import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MessageSquare, Building2, Eye, Trash2, Mail, Phone, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryDocs, setInquiryDocs] = useState([]);
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadContacts = () => axios.get(`${API}/admin/contacts`, { headers }).then(r => setContacts(r.data)).catch(() => {});
  const loadInquiries = () => axios.get(`${API}/admin/hall-inquiries`, { headers }).then(r => setInquiries(r.data)).catch(() => {});

  useEffect(() => { loadContacts(); loadInquiries(); }, []);

  const markRead = async (id) => {
    await axios.patch(`${API}/admin/contacts/${id}`, {}, { headers });
    loadContacts();
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Löschen?')) return;
    await axios.delete(`${API}/admin/contacts/${id}`, { headers });
    toast.success('Gelöscht');
    loadContacts();
  };

  const updateInquiryStatus = async (id, status) => {
    await axios.patch(`${API}/admin/hall-inquiries/${id}?status=${status}`, {}, { headers });
    toast.success('Status aktualisiert');
    loadInquiries();
  };

  const openInquiry = async (inq) => {
    setSelectedInquiry(inq);
    try {
      const res = await axios.get(`${API}/admin/customer-documents/${inq.id}`, { headers });
      setInquiryDocs(res.data);
    } catch { setInquiryDocs([]); }
  };

  const statusColors = { new: 'bg-blue-100 text-blue-700', processing: 'bg-amber-100 text-amber-700', done: 'bg-emerald-100 text-emerald-700' };

  return (
    <div data-testid="admin-contacts">
      <h1 className="text-2xl font-bold text-[#0F172A] mb-8">Anfragen verwalten</h1>

      <Tabs defaultValue="contacts">
        <TabsList className="mb-6">
          <TabsTrigger value="contacts" className="gap-2" data-testid="tab-contacts">
            <MessageSquare className="w-4 h-4" /> Kontaktanfragen ({contacts.length})
          </TabsTrigger>
          <TabsTrigger value="inquiries" className="gap-2" data-testid="tab-inquiries">
            <Building2 className="w-4 h-4" /> Hallenanfragen ({inquiries.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contacts">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Unternehmen</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map(c => (
                  <TableRow key={c.id} className={!c.read ? 'bg-blue-50/30' : ''} data-testid={`contact-row-${c.id}`}>
                    <TableCell>{c.read ? <Badge variant="outline">Gelesen</Badge> : <Badge className="bg-[#066ce7] text-white">Neu</Badge>}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{c.email}</TableCell>
                    <TableCell className="text-sm text-slate-500">{c.company || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE') : ''}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedContact(c); if (!c.read) markRead(c.id); }} data-testid={`view-contact-${c.id}`}><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteContact(c.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="inquiries">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Abmessung</TableHead>
                  <TableHead>Nutzung</TableHead>
                  <TableHead>Flache</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map(inq => (
                  <TableRow key={inq.id} data-testid={`inquiry-row-${inq.id}`}>
                    <TableCell>
                      <Select value={inq.status} onValueChange={v => updateInquiryStatus(inq.id, v)}>
                        <SelectTrigger className="w-28 h-8 text-xs">
                          <Badge className={statusColors[inq.status] || 'bg-slate-100'}>{inq.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Neu</SelectItem>
                          <SelectItem value="processing">In Bearbeitung</SelectItem>
                          <SelectItem value="done">Erledigt</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="font-medium">{inq.name}</TableCell>
                    <TableCell className="font-mono text-xs">{inq.length}x{inq.width}x{inq.height}m</TableCell>
                    <TableCell className="text-sm text-slate-500">{inq.usage}</TableCell>
                    <TableCell className="font-mono text-sm">{inq.area?.toLocaleString()} m2</TableCell>
                    <TableCell className="text-sm text-slate-500">{inq.created_at ? new Date(inq.created_at).toLocaleDateString('de-DE') : ''}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openInquiry(inq)} data-testid={`view-inquiry-${inq.id}`}><Eye className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Contact Detail Dialog */}
      <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kontaktanfrage</DialogTitle></DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="flex items-center gap-3"><span className="font-bold">{selectedContact.name}</span>{selectedContact.company && <Badge variant="outline">{selectedContact.company}</Badge>}</div>
              <div className="flex gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selectedContact.email}</span>
                {selectedContact.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selectedContact.phone}</span>}
              </div>
              <div className="bg-slate-50 rounded-lg p-4"><p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedContact.message}</p></div>
              <p className="text-xs text-slate-400">{selectedContact.created_at && new Date(selectedContact.created_at).toLocaleString('de-DE')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inquiry Detail Dialog */}
      <Dialog open={!!selectedInquiry} onOpenChange={() => { setSelectedInquiry(null); setInquiryDocs([]); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Hallenanfrage</DialogTitle></DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="font-bold text-lg">{selectedInquiry.name}</div>
              <div className="flex gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selectedInquiry.email}</span>
                {selectedInquiry.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selectedInquiry.phone}</span>}
              </div>
              <div className="bg-blue-50 rounded-lg p-5 space-y-2">
                <h4 className="font-bold text-[#066ce7]">Hallenspezifikation</h4>
                <p className="font-mono text-sm">{selectedInquiry.length}m x {selectedInquiry.width}m x {selectedInquiry.height}m</p>
                <p className="font-mono text-2xl font-bold text-[#066ce7]">{selectedInquiry.area?.toLocaleString()} m2</p>
                <p className="text-sm text-slate-600">Nutzung: {selectedInquiry.usage}</p>
                {selectedInquiry.features?.length > 0 && <p className="text-sm text-slate-600">Ausstattung: {selectedInquiry.features.join(', ')}</p>}
                {selectedInquiry.notes && <p className="text-sm text-slate-500 italic">{selectedInquiry.notes}</p>}
              </div>
              {inquiryDocs.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-[#0F172A] mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Kundendokumente ({inquiryDocs.length})</h4>
                  <div className="space-y-2">
                    {inquiryDocs.map(doc => (
                      <a key={doc.id} href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-slate-50 rounded-lg p-3 hover:bg-slate-100 transition-colors" data-testid={`admin-doc-${doc.id}`}>
                        <FileText className="w-4 h-4 text-[#066ce7]" />
                        <span className="text-sm text-[#066ce7]">{doc.original_name}</span>
                        <span className="text-xs text-slate-400 ml-auto">{doc.created_at ? new Date(doc.created_at).toLocaleDateString('de-DE') : ''}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
